import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import {
  AlertCircle, AlertTriangle, Clock, Pause, Play,
  RefreshCw, ShieldAlert, XCircle, Info, ExternalLink, Layers,
} from 'lucide-react';
import { api, LoiMayChu } from '../services/api';
import messages from '../locales/vi/joinCustomer.json';
import type { JoinBatchDTO } from '../services/kieu';
import { DauThe, NhanTrangThai, NutPhu, The, ChuaCoDuLieu } from './ui';
import {
  calculateBatchProgress,
  formatScheduleTime,
  getItemStatusInfo,
  getBatchStatusInfo,
  getPresetPolicySummary,
  translateJoinErrorCode,
} from '../services/groupJoinHelpers';

export interface JoinBatchPanelProps {
  actorUid: string | undefined;
  activeTab: string;
  onRefreshNeeded?: () => void;
  newBatchId?: string | null;
}

export function JoinBatchPanel({
  actorUid,
  activeTab,
  onRefreshNeeded,
  newBatchId,
}: JoinBatchPanelProps) {
  const [batches, setBatches] = useState<JoinBatchDTO[]>([]);
  const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null);
  const [dangTai, setDangTai] = useState(false);
  const [loi, setLoi] = useState<string | null>(null);
  const [resumeBlockedReason, setResumeBlockedReason] = useState<string | null>(null);
  const [dangThaoTac, setDangThaoTac] = useState(false);

  // Synchronous lock ref to prevent rapid double-clicks (UI-01)
  const actionLockRef = useRef(false);
  const activeActorUidRef = useRef(actorUid);
  activeActorUidRef.current = actorUid;

  // Polling backoff ref
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const backoffDelayRef = useRef<number>(5000);

  const fetchBatches = useCallback(async (isSilent = false) => {
    if (!actorUid) return;
    const requestedUid = actorUid;

    if (!isSilent) setDangTai(true);
    try {
      const res = await api.getJoinBatches(requestedUid);
      // Discard stale responses if actor changed while request was in-flight (IDENTITY-01)
      if (activeActorUidRef.current !== requestedUid) return;

      const list = res.batches || [];
      setBatches(list);
      setLoi(null);

      // Select active or requested batch
      setSelectedBatchId((current) => {
        if (newBatchId && list.some((b) => b.id === newBatchId)) {
          return newBatchId;
        }
        if (current && list.some((b) => b.id === current)) {
          return current;
        }
        return list[0]?.id || null;
      });
    } catch (err) {
      if (activeActorUidRef.current !== requestedUid) return;
      if (!isSilent) {
        setLoi(err instanceof LoiMayChu ? translateJoinErrorCode(err.ma, err.message) : 'Không tải được danh sách đợt tham gia.');
      }
    } finally {
      if (!isSilent) setDangTai(false);
    }
  }, [actorUid, newBatchId]);

  // Initial and actor-change fetch
  useEffect(() => {
    setBatches([]);
    setSelectedBatchId(null);
    setLoi(null);
    setResumeBlockedReason(null);
    backoffDelayRef.current = 5000;
    fetchBatches(false);
  }, [actorUid, fetchBatches]);

  // When a new batch ID is passed in, select it and refresh
  useEffect(() => {
    if (newBatchId) {
      setSelectedBatchId(newBatchId);
      backoffDelayRef.current = 5000;
      fetchBatches(true);
    }
  }, [newBatchId, fetchBatches]);

  const activeBatch = useMemo(() => {
    return batches.find((b) => b.id === selectedBatchId) || batches[0] || null;
  }, [batches, selectedBatchId]);

  // Check if batch has in-progress work
  const hasActiveWork = useMemo(() => {
    if (!activeBatch) return false;
    const s = String(activeBatch.status || '').toUpperCase();
    if (s === 'ACTIVE') return true;
    return activeBatch.items.some((it) => ['QUEUED', 'RUNNING'].includes(String(it.status).toUpperCase()));
  }, [activeBatch]);

  // Bounded Fallback Polling (UI-02)
  // ONLY runs when tab is active ('tham-gia'), active batch has pending work, and document is visible.
  useEffect(() => {
    const isTabActive = activeTab === 'tham-gia';

    function stopPolling() {
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    }

    function scheduleNextPoll() {
      stopPolling();
      if (!isTabActive || !hasActiveWork || document.visibilityState === 'hidden') {
        return;
      }

      pollTimerRef.current = setTimeout(async () => {
        if (!isTabActive || document.visibilityState === 'hidden') return;
        await fetchBatches(true);

        // Exponential backoff capped at 30 seconds
        backoffDelayRef.current = Math.min(30000, Math.round(backoffDelayRef.current * 1.5));
        scheduleNextPoll();
      }, backoffDelayRef.current);
    }

    function handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        backoffDelayRef.current = 5000;
        fetchBatches(true);
        scheduleNextPoll();
      } else {
        stopPolling();
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);

    if (isTabActive && hasActiveWork && document.visibilityState === 'visible') {
      scheduleNextPoll();
    } else {
      stopPolling();
    }

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [activeTab, hasActiveWork, fetchBatches]);

  // Control action handler with ref locking (UI-01)
  async function handleControl(action: 'pause' | 'resume' | 'cancel') {
    if (!activeBatch) return;
    if (actionLockRef.current) return;
    actionLockRef.current = true;
    setDangThaoTac(true);
    setResumeBlockedReason(null);

    try {
      const res = await api.controlJoinBatch(activeBatch.id, action);
      if (res.ok && res.batch) {
        setBatches((prev) => prev.map((b) => (b.id === res.batch.id ? res.batch : b)));
        if (onRefreshNeeded) onRefreshNeeded();
      }
    } catch (err: any) {
      if (err instanceof LoiMayChu && (err.trangThai === 409 || err.ma === 'CLEARANCE_REQUIRED' || err.ma === 'ACTOR_JOIN_BLOCKED')) {
        setResumeBlockedReason(
          'Không thể tiếp tục: Tài khoản đang bị hạn chế (checkpoint/giới hạn tần suất) hoặc chưa xác định kết quả. Cần kiểm tra và xác minh tài khoản Facebook trước khi tiếp tục.'
        );
      } else {
        const msg = err instanceof LoiMayChu ? translateJoinErrorCode(err.ma, err.message) : 'Thao tác điều khiển thất bại.';
        setLoi(msg);
      }
    } finally {
      actionLockRef.current = false;
      setDangThaoTac(false);
    }
  }

  // Refresh manually with ref lock
  async function handleManualRefresh() {
    if (actionLockRef.current) return;
    actionLockRef.current = true;
    setDangThaoTac(true);
    try {
      backoffDelayRef.current = 5000;
      await fetchBatches(false);
      if (onRefreshNeeded) onRefreshNeeded();
    } finally {
      actionLockRef.current = false;
      setDangThaoTac(false);
    }
  }

  if (dangTai && batches.length === 0) {
    return (
      <The className="p-5">
        <div className="flex items-center gap-3 text-slate-500 dark:text-slate-400">
          <RefreshCw className="h-4 w-4 animate-spin text-brand-600" />
          <span className="text-sm">Đang tải tiến trình tham gia nhóm...</span>
        </div>
      </The>
    );
  }

  if (batches.length === 0) {
    return (
      <The className="overflow-hidden">
        <DauThe
          icon={<Layers className="h-4 w-4 text-brand-600" />}
          tieuDe="Tiến độ tham gia nhóm tự động"
          phai={
            <NutPhu onClick={handleManualRefresh} dangChay={dangThaoTac}>
              <RefreshCw className="h-3.5 w-3.5" />
              Làm mới
            </NutPhu>
          }
        />
        <div className="p-5">
          <ChuaCoDuLieu
            chu="Chưa có đợt tham gia nhóm nào."
            goiY="Chọn các nhóm ứng viên ở trên và nhấn 'Gửi yêu cầu' để bắt đầu đợt tham gia theo lịch an toàn."
          />
        </div>
      </The>
    );
  }

  const progress = calculateBatchProgress(activeBatch?.items || []);
  const policySummary = getPresetPolicySummary(activeBatch?.policy);
  const schedule = formatScheduleTime(activeBatch?.nextEligibleAt);
  const batchStatus = getBatchStatusInfo(activeBatch?.status || 'UNKNOWN');

  const isBlocked = Boolean(activeBatch?.status?.startsWith('BLOCKED_'));
  const canPause = activeBatch?.status === 'ACTIVE';
  const canResume = activeBatch?.status === 'PAUSED';
  const canCancel = activeBatch?.status === 'ACTIVE' || activeBatch?.status === 'PAUSED' || isBlocked;

  return (
    <div className="space-y-4">
      <The className="overflow-hidden">
        <DauThe
          icon={<Layers className="h-4 w-4 text-brand-600" />}
          tieuDe="Tiến độ đợt tham gia nhóm"
          dem={batches.length}
          phai={
            <div className="flex items-center gap-2">
              <NutPhu onClick={handleManualRefresh} dangChay={dangThaoTac}>
                <RefreshCw className="h-3.5 w-3.5" />
                Làm mới
              </NutPhu>
            </div>
          }
        />

        <div className="space-y-5 p-5">
          {/* Batches selector tab pills if multiple batches exist */}
          {batches.length > 1 && (
            <div className="flex flex-wrap gap-2 border-b border-slate-100 pb-3 dark:border-slate-700/60">
              {batches.slice(0, 8).map((b) => {
                const s = getBatchStatusInfo(b.status);
                const isSelected = b.id === activeBatch?.id;
                return (
                  <button
                    key={b.id}
                    onClick={() => setSelectedBatchId(b.id)}
                    className={
                      'flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-semibold transition ' +
                      (isSelected
                        ? 'bg-brand-50 text-brand-700 ring-1 ring-brand-500 dark:bg-brand-500/10 dark:text-brand-300'
                        : 'bg-slate-50 text-slate-600 hover:bg-slate-100 dark:bg-slate-800 dark:text-slate-300')
                    }
                  >
                    <span>Đợt {b.id.slice(-6)}</span>
                    <NhanTrangThai chu={s.text} mau={s.color} />
                  </button>
                );
              })}
            </div>
          )}

          {/* Error Banner */}
          {loi && (
            <div className="flex items-start gap-2.5 rounded-xl border border-rose-200 bg-rose-50 p-3.5 text-sm text-rose-800 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-400">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" />
              <span>{loi}</span>
            </div>
          )}

          {/* Blocked 409 Resume Conflict Notice */}
          {resumeBlockedReason && (
            <div className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 p-3.5 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <div className="space-y-1">
                <p className="font-semibold">Không thể tiếp tục thực thi:</p>
                <p>{resumeBlockedReason}</p>
              </div>
            </div>
          )}

          {/* Batch Header & Controls */}
          {activeBatch && (
            <div className="rounded-xl border border-slate-200/80 bg-slate-50/70 p-4 dark:border-slate-700/60 dark:bg-slate-900/40">
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <NhanTrangThai chu={batchStatus.text} mau={batchStatus.color} />
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {new Date(activeBatch.createdAt).toLocaleString('vi-VN')}
                  </p>
                  <details className="text-xs text-slate-500"><summary>{messages.reference}</summary><span className="font-mono">{activeBatch.id} · {activeBatch.actorUid}</span></details>
                </div>

                {/* Batch Control Buttons */}
                <div className="flex flex-wrap items-center gap-2">
                  {canPause && (
                    <button
                      onClick={() => handleControl('pause')}
                      disabled={dangThaoTac}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100 disabled:opacity-50 dark:border-amber-500/30 dark:bg-amber-500/20 dark:text-amber-300"
                    >
                      <Pause className="h-3.5 w-3.5" />
                      Tạm dừng
                    </button>
                  )}

                  {canResume && (
                    <button
                      onClick={() => handleControl('resume')}
                      disabled={dangThaoTac}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-800 hover:bg-emerald-100 disabled:opacity-50 dark:border-emerald-500/30 dark:bg-emerald-500/20 dark:text-emerald-300"
                    >
                      <Play className="h-3.5 w-3.5" />
                      Tiếp tục
                    </button>
                  )}

                  {canCancel && (
                    <button
                      onClick={() => handleControl('cancel')}
                      disabled={dangThaoTac}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-rose-300 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-800 hover:bg-rose-100 disabled:opacity-50 dark:border-rose-500/30 dark:bg-rose-500/20 dark:text-rose-300"
                    >
                      <XCircle className="h-3.5 w-3.5" />
                      Hủy đợt
                    </button>
                  )}
                </div>
              </div>

              {/* Explicit Note: running jobs cannot be cancelled midway */}
              <div className="mt-3 flex items-start gap-2 rounded-lg bg-slate-100/80 p-2.5 text-[11px] text-slate-600 dark:bg-slate-800/80 dark:text-slate-400">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-500" />
                <span>
                  Lưu ý: Nhóm đang thực thi trên Facebook không thể hủy giữa chừng; thao tác hủy sẽ áp dụng cho các nhóm còn đang chờ trong hàng đợi.
                </span>
              </div>
            </div>
          )}

          {/* Schedule Banner: Server clock authority notice */}
          {schedule.hasSchedule && (
            <div className="flex items-start gap-3 rounded-xl border border-sky-200 bg-sky-50/80 p-3.5 text-sm text-sky-900 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-300">
              <Clock className="mt-0.5 h-4 w-4 shrink-0 text-sky-600 dark:text-sky-400" />
              <div className="space-y-0.5">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold">Lịch thực thi dự kiến:</span>
                  <span className="font-mono font-bold text-sky-700 dark:text-sky-300">{schedule.formatted}</span>
                </div>
                <p className="text-xs text-sky-600/90 dark:text-sky-400/80">{schedule.driftNotice}</p>
              </div>
            </div>
          )}

          <details className="rounded-lg border border-slate-200 p-3 text-xs dark:border-slate-700">
          <summary className="cursor-pointer text-slate-500">{messages.details}</summary>
          {/* Historical snapshots remain available for support, not editable. */}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-xl border border-slate-200/80 bg-white p-3 shadow-sm dark:border-slate-700/60 dark:bg-slate-800">
              <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-400">Giãn cách an toàn</span>
              <p className="mt-1 font-semibold text-slate-900 dark:text-white">{policySummary.delayText}</p>
            </div>
            <div className="rounded-xl border border-slate-200/80 bg-white p-3 shadow-sm dark:border-slate-700/60 dark:bg-slate-800">
              <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-400">Quy mô hiệp</span>
              <p className="mt-1 font-semibold text-slate-900 dark:text-white">{policySummary.roundText}</p>
            </div>
            <div className="rounded-xl border border-slate-200/80 bg-white p-3 shadow-sm dark:border-slate-700/60 dark:bg-slate-800">
              <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-400">Nghỉ giữa hiệp</span>
              <p className="mt-1 font-semibold text-slate-900 dark:text-white">{policySummary.restText}</p>
            </div>
            {false && policySummary.quotaText && (
              <div className="rounded-xl border border-slate-200/80 bg-white p-3 shadow-sm dark:border-slate-700/60 dark:bg-slate-800">
                <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-400">Hạn mức máy chủ</span>
                <p className="mt-1 font-semibold text-slate-900 dark:text-white">{policySummary.quotaText}</p>
              </div>
            )}
          </div>

          {/* Progress Bar & Counters */}
          </details>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs font-semibold">
              <span className="text-slate-700 dark:text-slate-300">
                Tiến độ đợt: {progress.completed}/{progress.total} nhóm
              </span>
              <span className="font-mono text-brand-600 dark:text-brand-400">{progress.percent}%</span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
              <div
                className="h-full bg-brand-600 transition-all duration-300 dark:bg-brand-500"
                style={{ width: `${progress.percent}%` }}
              />
            </div>
            <div className="flex flex-wrap gap-3 pt-1 text-[11px] text-slate-500 dark:text-slate-400">
              <span>Đã xong: <strong className="text-emerald-600">{progress.completed}</strong></span>
              {progress.running > 0 && <span>Đang chạy: <strong className="text-sky-600">{progress.running}</strong></span>}
              {progress.queued > 0 && <span>Đang chờ: <strong className="text-slate-600 dark:text-slate-300">{progress.queued}</strong></span>}
              {progress.skipped > 0 && <span>Đã bỏ qua: <strong>{progress.skipped}</strong></span>}
              {progress.failed > 0 && <span>Thất bại/chặn: <strong className="text-rose-600">{progress.failed}</strong></span>}
              {progress.cancelled > 0 && <span>Đã hủy: <strong>{progress.cancelled}</strong></span>}
            </div>
          </div>

          {/* Items Table / List */}
          <div className="space-y-2">
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Danh sách nhóm trong đợt ({activeBatch?.items.length || 0})
            </h4>

            <div className="divide-y divide-slate-100 rounded-xl border border-slate-200/80 bg-white dark:divide-slate-700/60 dark:border-slate-700/60 dark:bg-slate-800">
              {activeBatch?.items.map((item, idx) => {
                const statusInfo = getItemStatusInfo(item.status);
                const isRunning = String(item.status).toUpperCase() === 'RUNNING';
                const isUnknown = ['UNKNOWN', 'UNKNOWN_RESULT'].includes(String(item.status).toUpperCase());
                const isNeedsAction = String(item.status).toUpperCase() === 'NEEDS_USER_ACTION';

                return (
                  <div key={item.id || item.groupId || idx} className="p-3.5 transition hover:bg-slate-50/60 dark:hover:bg-slate-700/30">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <div className="min-w-0 space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-slate-900 dark:text-white">
                            Nhóm {item.groupId}
                          </span>
                          <a
                            href={`https://www.facebook.com/groups/${item.groupId}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-slate-400 hover:text-brand-600"
                            title="Mở nhóm trên Facebook"
                          >
                            <ExternalLink className="h-3 w-3" />
                          </a>
                          <NhanTrangThai chu={statusInfo.text} mau={statusInfo.color} />
                          {isRunning && (
                            <RefreshCw className="h-3 w-3 animate-spin text-brand-600" />
                          )}
                        </div>

                        {/* Status Description or Backend Message */}
                        <div className="text-xs text-slate-500 dark:text-slate-400">
                          {item.message ? item.message : statusInfo.description}
                        </div>

                        {/* Special Notice for NEEDS_USER_ACTION */}
                        {isNeedsAction && (
                          <div className="mt-1 inline-flex items-center gap-1.5 rounded bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-800 dark:bg-amber-500/10 dark:text-amber-300">
                            <AlertTriangle className="h-3 w-3 text-amber-600" />
                            Nhóm yêu cầu trả lời câu hỏi khảo sát trên Facebook (cần thao tác thủ công)
                          </div>
                        )}

                        {/* Special Notice for UNKNOWN: no retry */}
                        {isUnknown && (
                          <div className="mt-1 inline-flex items-center gap-1.5 rounded bg-slate-100 px-2 py-1 text-[11px] font-medium text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                            <Info className="h-3 w-3 text-slate-500" />
                            {messages.unknown}
                          </div>
                        )}
                      </div>

                      <div className="flex shrink-0 items-center gap-2 text-right text-xs text-slate-500 dark:text-slate-400">
                        {item.jobId && (
                          <details className="text-[11px]"><summary>{messages.reference}</summary><span className="font-mono">{item.jobId.slice(-8)}</span></details>
                        )}
                        {item.nextEligibleAt && (
                          <span className="font-mono text-[11px]">
                            {formatScheduleTime(item.nextEligibleAt).formatted}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </The>
    </div>
  );
}
